"use client";

import type React from "react";

import { graphql, useFragment } from "../../../../__generated__/gql";
import type { FragmentType } from "../../../../__generated__/gql";
import { CalendarIcon } from "../../dx-icons";
import { Link } from "../../ui/link";
import { buildReserveCtaLabels } from "../_utils/build-reserve-cta-labels";

/**
 * CTA で「予約する」ボタンを表示するために必要な Availability 上のデータ。
 * Fragment Colocation: 必要なフィールドを CtaButton 自身が宣言する。
 * 親 (availability-cell など) はこれを compose したクエリを投げる。
 */
export const AvailabilityOnCtaButton = graphql(`
  fragment AvailabilityOnCtaButton on AccommodationAvailability {
    date
    guaranteedPrice
  }
`);

/**
 * CTA ボタンが取りうる Action。
 *
 * - `reserve`: 日付と価格を持つ予約 CTA。availability fragment data から日付/価格を導出する
 * - `close`:  単純な「閉じる」ボタン。label は任意で上書き可能 (デフォルト "閉じる")
 * - `proceed`: roomType 経路の予約進行 CTA。表示用ラベル/価格を注入側から受け取る（reserve 非依存）
 * - `navigate-with-icon`: Link 遷移 + 右カレンダーアイコン。availability 非依存で、
 *   ラベル + サブラベルを受け取り href に遷移する。エリア/拠点/部屋タイプページから
 *   予約カレンダーへの動線に使う
 */
export type CtaAction =
  | {
      kind: "reserve";
      onAction: () => void | Promise<void>;
      loading?: boolean;
      /** loading 中はデータ未取得でも CTA を出すため optional */
      availabilityFragmentData?: FragmentType<typeof AvailabilityOnCtaButton>;
      /** PC Popover のアンカー用。Popover の `anchor` prop に渡す。 */
      buttonRef?: React.Ref<HTMLButtonElement>;
    }
  | {
      kind: "close";
      onAction: () => void | Promise<void>;
      label?: string;
    }
  | {
      kind: "proceed";
      label: string;
      /** 「YYYY/MM/DDからN泊」「合計」などの表示用ラベル */
      subLabel?: string;
      /** 「¥81,360」など */
      priceLabel?: string;
      /** 価格取得中は pulse スケルトンを表示する */
      priceLoading?: boolean;
      /** 非活性化する（日程未確定など）。 */
      disabled?: boolean;
      onAction: () => void | Promise<void>;
      /** PC Popover のアンカー用。Popover の `anchor` prop に渡す。 */
      buttonRef?: React.Ref<HTMLButtonElement>;
    }
  | {
      kind: "navigate-with-icon";
      /** 遷移先 URL。 内部リンク / 外部 URL の両方を許容する。 */
      href: string;
      /** メインラベル (例: 「予約する」)。 16px bold。 */
      label: string;
      /** サブラベル (例: 「伊豆エリアの拠点」)。 12px regular。 */
      subLabel: string;
    };

type Props = { action: CtaAction };

/**
 * 部屋詳細フッターの CTA ボタン。
 *
 * スクロールに追従させるため `sticky bottom-0` で配置。
 * iOS Home Indicator 領域を避けるため、底部 padding に `env(safe-area-inset-bottom)` を加算する。
 */
export function CtaButton({ action }: Props) {
  return (
    <div className="bg-dx-surface-base sticky bottom-0 z-10 px-[16px] pt-[16px] pb-[16px]">
      {action.kind === "reserve" ? (
        <ReserveCta action={action} />
      ) : action.kind === "proceed" ? (
        <ProceedCta action={action} />
      ) : action.kind === "navigate-with-icon" ? (
        <NavigateWithIconCta action={action} />
      ) : (
        <CloseCta action={action} />
      )}
    </div>
  );
}

/**
 * 「予約する」CTA: ダーク背景 + ラベル + 日付 + 価格 + カレンダーアイコン。
 * Availability fragment から内部で date / price を導出する。
 *
 * loading 中またはデータ未取得時は「予約する」文言を残したままスケルトン2本を表示する。
 * useFragment はフックなので条件分岐不可 — データあり時のみ ReserveCtaInfo で呼び出す。
 */
function ReserveCta({
  action,
}: {
  action: Extract<CtaAction, { kind: "reserve" }>;
}) {
  const { onAction, loading, availabilityFragmentData, buttonRef } = action;

  return (
    <button
      data-analytics-id="room_detail_clicked_reserve"
      ref={buttonRef}
      type="button"
      onClick={onAction}
      disabled={loading}
      // Figma は outer flex に gap-[2px]。text 領域と divider の間に少し息継ぎを入れる。
      className="bg-dx-button-dark-fill flex h-[52px] w-full items-center justify-center gap-[2px] overflow-hidden rounded-full py-[6px] pr-[4px] pl-[12px] shadow-[0px_6px_14px_rgba(0,0,0,0.1),0px_0px_0px_rgba(0,0,0,0.02)] disabled:opacity-60"
    >
      <div className="flex flex-1 items-center justify-center gap-[12px]">
        <span className="font-notojp text-dx-base-white text-[16px] leading-[1.6] font-bold">
          予約する
        </span>
        {loading === true || availabilityFragmentData == null ? (
          <div className="flex items-center gap-[8px]">
            <span className="bg-dx-base-white/[0.18] h-[17px] w-[63px] animate-pulse rounded-[4px]" />
            <span className="bg-dx-base-white/[0.18] h-[11px] w-[73px] animate-pulse rounded-[4px]" />
          </div>
        ) : (
          <ReserveCtaInfo availabilityFragmentData={availabilityFragmentData} />
        )}
      </div>
      <div className="bg-dx-stone-600 h-[32px] w-px shrink-0" />
      <div className="text-dx-base-white flex h-full w-[48px] shrink-0 items-center justify-center">
        <CalendarIcon />
      </div>
    </button>
  );
}

/**
 * 日付・価格表示部分。useFragment を呼ぶためコンポーネントに切り出す。
 * loading / データなし時は呼ばれない (スケルトン表示になる)。
 */
function ReserveCtaInfo({
  availabilityFragmentData,
}: {
  availabilityFragmentData: FragmentType<typeof AvailabilityOnCtaButton>;
}) {
  const availability = useFragment(
    AvailabilityOnCtaButton,
    availabilityFragmentData,
  );
  const { dateLabel, priceLabel } = buildReserveCtaLabels({
    date: availability.date,
    guaranteedPrice: availability.guaranteedPrice,
  });

  return (
    <div className="text-dx-base-white flex items-center gap-[8px]">
      <span className="font-notojp text-[12px] leading-[1.6]">{dateLabel}</span>
      {priceLabel && (
        // 価格は Latin/数字のみ。プロジェクトの数字慣例に従い font-roboto。
        <span className="font-roboto text-[16px] leading-[1.6]">
          {priceLabel}
        </span>
      )}
    </div>
  );
}

/**
 * roomType 経路の「予約する」CTA。
 * reserve と異なり availability fragment に依存せず、注入された表示ラベルを描くだけ。
 * priceLoading 時は価格部分に pulse スケルトンを出す。
 */
function ProceedCta({
  action,
}: {
  action: Extract<CtaAction, { kind: "proceed" }>;
}) {
  const {
    label,
    subLabel,
    priceLabel,
    priceLoading,
    disabled,
    onAction,
    buttonRef,
  } = action;

  return (
    <button
      data-analytics-id="room_type_reserve_clicked_proceed"
      ref={buttonRef}
      type="button"
      data-testid="reserve-cta"
      onClick={onAction}
      disabled={disabled}
      className="bg-dx-button-dark-fill flex h-[52px] w-full items-center justify-center gap-[2px] overflow-hidden rounded-full py-[6px] pr-[12px] pl-[12px] shadow-[0px_6px_14px_rgba(0,0,0,0.1),0px_0px_0px_rgba(0,0,0,0.02)] disabled:opacity-60"
    >
      <div className="flex flex-1 items-center justify-center gap-[12px]">
        <span className="font-notojp text-dx-base-white text-[16px] leading-[1.6] font-bold">
          {label}
        </span>
        {(subLabel != null || priceLabel != null || priceLoading === true) && (
          <div className="text-dx-base-white flex items-center gap-[8px]">
            {subLabel != null && (
              <span className="font-notojp text-[12px] leading-[1.6]">
                {subLabel}
              </span>
            )}
            {priceLoading === true ? (
              <span className="bg-dx-base-white/[0.18] h-[11px] w-[73px] animate-pulse rounded-[4px]" />
            ) : (
              priceLabel != null && (
                // 主に Stay ticket では清掃費など予約後に加算される費用があるため、
                // 確定額と誤認されないよう「〜」を付ける。
                <span className="font-roboto text-[16px] leading-[1.6]">
                  {priceLabel}〜
                </span>
              )
            )}
          </div>
        )}
      </div>
    </button>
  );
}

/**
 * Link 遷移 + 右カレンダーアイコン付き CTA。
 * 予約 (`reserve`) と同じ視覚設計だが、availability data に依存せず
 * サブラベルを直接受け取る。 エリア/拠点/部屋タイプページから
 * 予約カレンダーへの動線 (`/go/reserve?...`) に使う。
 */
function NavigateWithIconCta({
  action,
}: {
  action: Extract<CtaAction, { kind: "navigate-with-icon" }>;
}) {
  const { href, label, subLabel } = action;

  return (
    <Link
      data-analytics-id="reservation_cta_clicked_reserve"
      href={href}
      className="bg-dx-button-dark-fill flex h-[52px] w-full items-center justify-center gap-[2px] overflow-hidden rounded-full py-[6px] pr-[4px] pl-[12px] shadow-[0px_6px_14px_rgba(0,0,0,0.1),0px_0px_0px_rgba(0,0,0,0.02)]"
    >
      <div className="flex flex-1 items-center justify-center gap-[12px]">
        <span className="font-notojp text-dx-base-white text-[16px] leading-[1.6] font-bold">
          {label}
        </span>
        <span className="font-notojp text-dx-base-white text-[12px] leading-[1.6]">
          {subLabel}
        </span>
      </div>
      <div className="bg-dx-stone-700 h-[32px] w-px shrink-0" />
      <div className="text-dx-base-white flex h-full w-[48px] shrink-0 items-center justify-center">
        <CalendarIcon />
      </div>
    </Link>
  );
}

/**
 * 「閉じる」CTA: アイコン / 日付情報を持たないシンプルなボタン。
 * Drawer をセル経由以外で開いたケース (availability 不明) や、
 * 予約不可なケースに使う。
 */
function CloseCta({
  action,
}: {
  action: Extract<CtaAction, { kind: "close" }>;
}) {
  return (
    <button
      // 閉じるだけで、押されなかったことに情報が無い
      data-analytics-ignore
      type="button"
      onClick={action.onAction}
      className="bg-dx-button-dark-fill font-notojp text-dx-base-white flex h-[52px] w-full items-center justify-center rounded-full text-[16px] leading-[1.6] font-bold shadow-[0px_6px_14px_rgba(0,0,0,0.1),0px_0px_0px_rgba(0,0,0,0.02)]"
    >
      {action.label ?? "閉じる"}
    </button>
  );
}
